/**
 * CetusEd Community Portal — Meeting/Event Calendar
 *
 * COMPLIANCE: IDEA §300.322 requires schools to ensure parent participation
 *   in IEP team meetings. The calendar displays scheduled IEP meetings,
 *   evaluation conferences, 504 meetings, and other school events.
 *   Parents must receive notice of meetings early enough to participate.
 *
 * MIGRATION TARGET: The monolith has no parent-facing calendar view.
 *   Meeting scheduling is handled through the PRD form (S15) with
 *   date fields. This component provides the read-only parent view.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useCalendarEvents } from '../../api/hooks';
import { Button } from '../../ui/components/Button';
import { Card } from '../../ui/components/Card';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';

type ViewMode = 'month' | 'week' | 'list';

export const Calendar: React.FC = () => {
  const auth = useAuth();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    auth.user?.linkedStudents[0]?.id || null,
  );

  const now = useMemo(() => new Date(), []);
  const startOfMonth = useMemo(
    () => new Date(now.getFullYear(), now.getMonth(), 1).toISOString(),
    [now],
  );
  const endOfMonth = useMemo(
    () => new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString(),
    [now],
  );

  const { data: events, isLoading, error } = useCalendarEvents(
    startOfMonth,
    endOfMonth,
    selectedStudentId || undefined,
  );

  const handleStudentChange = useCallback((studentId: string) => {
    setSelectedStudentId(studentId);
  }, []);

  if (!auth.user) return null;

  return (
    <div className="calendar-page">
      <div className="page-header">
        <h1>Calendar</h1>
        <Link to="/calendar/request">
          <Button>Request Meeting</Button>
        </Link>
      </div>

      {auth.user.linkedStudents.length > 1 && (
        <div className="student-selector">
          <label htmlFor="cal-student-select">Select Student:</label>
          <select
            id="cal-student-select"
            value={selectedStudentId || ''}
            onChange={(e) => handleStudentChange(e.target.value)}
          >
            {auth.user.linkedStudents.map((student) => (
              <option key={student.id} value={student.id}>
                {student.name} — {student.school}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="calendar-view-controls">
        <Button
          variant={viewMode === 'list' ? 'primary' : 'ghost'}
          onClick={() => setViewMode('list')}
        >
          List
        </Button>
        <Button
          variant={viewMode === 'month' ? 'primary' : 'ghost'}
          onClick={() => setViewMode('month')}
        >
          Month
        </Button>
      </div>

      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="error-message" role="alert">
          Unable to load calendar events. Please try again later.
        </div>
      )}

      {events && events.length === 0 && (
        <p className="empty-state">
          No upcoming events. You can request a meeting with school staff.
        </p>
      )}

      {events && events.length > 0 && viewMode === 'list' && (
        <div className="event-list">
          {events.map((event) => (
            <Card key={event.id}>
              <div className="event-card-content">
                <div className="event-date-badge">
                  <span className="event-date-month">
                    {new Date(event.start).toLocaleString('default', { month: 'short' })}
                  </span>
                  <span className="event-date-day">
                    {new Date(event.start).getDate()}
                  </span>
                </div>
                <div className="event-details">
                  <h3>{event.title}</h3>
                  <p className="event-time">
                    {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {' — '}
                    {new Date(event.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {event.location && <p className="event-location">{event.location}</p>}
                  <span className={`event-status event-status--${event.status}`}>
                    {event.status}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {events && events.length > 0 && viewMode === 'month' && (
        <div className="calendar-month-view">
          <p className="placeholder-note">Month view grid rendering calendar days.</p>
        </div>
      )}
    </div>
  );
};
